import React, { FC, useState } from 'react';
import { PlayerInfo } from '@/screens/authenticated/types';
import { SharePanel } from './share-panel';
import { Link } from 'react-router-dom';

export interface LobbyProps {
  roomID: string;
  seats: number;
  players: PlayerInfo[];
  meID: string;
  meName: string;
  isHost: boolean;
  hostName?: string;
  onFillWithBots?: () => Promise<void>;
  onLeave: () => void;
}

const initial = (name: string) => (name?.trim()?.[0] || '?').toUpperCase();

export const Lobby: FC<LobbyProps> = ({
  roomID,
  seats,
  players,
  meID,
  meName,
  isHost,
  hostName,
  onFillWithBots,
  onLeave,
}) => {
  const [botsBusy, setBotsBusy] = useState(false);
  const [tab, setTab] = useState<'invite' | 'players'>('invite');
  const seated = players.length;
  const empty = Math.max(0, seats - seated);
  const sorted = [...players].sort((a, b) => a.entryNo - b.entryNo);

  return (
    <div className="st-lobby st-stack" style={{ ['--st-gap' as string]: '12px' }}>
      <section className="st-stack">
        <p className="st-eyebrow">Lobby · {seats} seats</p>
        <h1 className="st-h2">
          {empty === 0
            ? 'Everyone is here. Dealing…'
            : `Waiting for ${empty} more player${empty === 1 ? '' : 's'}`}
        </h1>
        <p className="st-lead">The game starts automatically when all {seats} seats fill.</p>
        <div
          className="st-progress"
          role="progressbar"
          aria-label="Seats filled"
          aria-valuemin={0}
          aria-valuemax={seats}
          aria-valuenow={seated}
          aria-valuetext={`${seated} of ${seats} seats filled`}
        >
          <span style={{ width: `${(seated / Math.max(seats, 1)) * 100}%` }} />
        </div>
      </section>

      <nav className="cw-screen-tabs" aria-label="Lobby sections" style={{gridTemplateColumns: '1fr 1fr'}}>
        <button type="button" aria-pressed={tab === 'invite'} onClick={() => setTab('invite')}>Invite link</button>
        <button type="button" aria-pressed={tab === 'players'} onClick={() => setTab('players')}>Players ({seated}/{seats})</button>
      </nav>
      {tab === 'invite' && <div className="st-card" data-suit="hearts">
        <SharePanel
          roomID={roomID}
          seats={seats}
          hostName={isHost ? meName : hostName}
        />
      </div>}

      {tab === 'players' && <section className="st-panel st-stack" aria-labelledby="lobby-seats">
        <h2 id="lobby-seats" className="st-h3">
          At the table ({seated}/{seats})
        </h2>
        <ul className="st-seat-grid">
          {sorted.map(p => {
            const isMe = p.id === meID;
            return (
              <li
                key={p.id}
                className={`st-seat-tile ${isMe ? 'st-seat-tile--me' : ''}`}
              >
                <span className="st-seat-tile__avatar" aria-hidden="true">
                  {p.userType === 'bot' ? '🤖' : initial(p.name)}
                </span>
                <span className="st-seat-tile__name">
                  {p.name}
                  {isMe ? ' (you)' : ''}
                </span>
                <span className="st-small st-muted">
                  {p.entryNo === 1 || (isMe && isHost)
                    ? 'Host'
                    : p.userType === 'bot'
                      ? 'Bot'
                      : p.state === 'offline'
                        ? 'Away'
                        : 'Seated'}
                </span>
              </li>
            );
          })}
          {Array.from({ length: empty }).map((_, i) => (
            <li key={`empty-${i}`} className="st-seat-tile st-seat-tile--empty">
              <span className="st-seat-tile__avatar" aria-hidden="true">
                +
              </span>
              <span className="st-seat-tile__name">Open seat</span>
            </li>
          ))}
        </ul>

        {isHost && empty > 0 && onFillWithBots && (
          <div className="st-stack" style={{ ['--st-gap' as string]: '8px' }}>
            <button
              type="button"
              className="st-btn st-btn--secondary st-btn--block"
              disabled={botsBusy}
              aria-busy={botsBusy}
              onClick={async () => {
                setBotsBusy(true);
                try {
                  await onFillWithBots();
                } finally {
                  setBotsBusy(false);
                }
              }}
            >
              {botsBusy
                ? 'Seating bots…'
                : `Fill ${empty} open seat${empty === 1 ? '' : 's'} with bots`}
            </button>

          </div>
        )}
      </section>}

      <Link to="/game-rules" className="st-link st-small st-lobby-rules">How to play</Link>

      <button
        type="button"
        className="st-btn st-btn--danger st-btn--block"
        onClick={onLeave}
      >
        Leave table
      </button>
    </div>
  );
};
